import { PlayerServiceAPI } from '@API/data-services/player-service'
import { ResponseError, PamPlayerSearchSummary } from '@Generated/back-office-client/src'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import {
  Button,
  Container,
  Flex,
  Heading,
  Section,
  Skeleton,
  TextField,
  Text,
} from '@radix-ui/themes'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import './styles.scss'
import { useNavigate } from 'react-router-dom'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { RootState } from '@AppState/redux-store'
import { Routes } from '@Components/main-app/types'
import { usePlayerCapabilities } from './use-player-capabilities'
import { PlayerSearchListView } from './player-search-list-view'

const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 300
const MIN_SEARCH_LENGTH = 3

export const PlayerSearch = () => {
  const { data: capabilities, isLoading } = usePlayerCapabilities()

  if (isLoading) {
    return (
      <Container className="player-search-container">
        <Section>
          <Skeleton loading>
            <Heading as="h1" size="5">
              Player Lookup
            </Heading>
          </Skeleton>
        </Section>
      </Container>
    )
  }

  if (capabilities?.isEditable) {
    return <PamPlayerSearch />
  }

  return <VendorPlayerSearch />
}

const PamPlayerSearch = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const nav = useNavigate()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(0)

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [search])

  const effectiveSearch =
    debouncedSearch.trim().length >= MIN_SEARCH_LENGTH ? debouncedSearch.trim() : ''

  const { data, isLoading } = useQuery({
    queryKey: [`${activeCustomerId}-player-search`, effectiveSearch, currentPage],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    enabled: effectiveSearch !== '',
    queryFn: () =>
      PlayerServiceAPI.searchPlayers(
        effectiveSearch,
        currentPage * PAGE_SIZE,
        currentPage * PAGE_SIZE + PAGE_SIZE,
      ),
  })

  const onSearchChange = (next: string) => {
    setSearch(next)
    setCurrentPage(0)
  }

  const onRowClick = (row: PamPlayerSearchSummary) => {
    nav(Routes.PLAYER_DETAILS.replace(':lookupId', row.lookupId))
  }

  return (
    <Container className="player-search-container">
      <Section>
        <Flex direction="column" gap="4">
          <Heading as="h1" size="5">
            Player Lookup
          </Heading>
          <Heading as="h2" size="3">
            Search for a player by name, email, or id.
          </Heading>
          <PlayerSearchListView
            page={effectiveSearch === '' ? undefined : data}
            search={search}
            minSearchLength={MIN_SEARCH_LENGTH}
            pageSize={PAGE_SIZE}
            currentPage={currentPage}
            isLoading={isLoading && effectiveSearch !== ''}
            onSearchChange={onSearchChange}
            onPageChange={setCurrentPage}
            onRowClick={onRowClick}
          />
        </Flex>
      </Section>
    </Container>
  )
}

const VendorPlayerSearch = () => {
  const nav = useNavigate()
  const [playerSearchId, setPlayerSearchId] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string | null>()

  const { mutate: searchPlayer, isPending } = useMutation({
    mutationFn: async () => {
      setErrorMessage(null)
      try {
        const details = await PlayerServiceAPI.getPlayerByLookup(playerSearchId)
        nav(Routes.PLAYER_DETAILS.replace(':lookupId', details.player.lookupId), {
          state: details,
        })
      } catch (e) {
        if (e instanceof ResponseError) {
          setErrorMessage((await e.response.json()).message)
        }
      }
    },
  })

  return (
    <Container className="player-search-container">
      <Section>
        <Flex direction="column" gap="3">
          <Heading as="h1" size="5">
            Player Lookup
          </Heading>
          <Heading as="h2" size="3">
            Search for a player to locate their details and activity.
          </Heading>

          <Flex gap="3">
            <TextField.Root
              className="player-id-lookup-field"
              placeholder="Search for a player by id"
              value={playerSearchId}
              onChange={(e) => {
                setPlayerSearchId(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && playerSearchId) {
                  searchPlayer()
                }
              }}>
              <TextField.Slot>
                <MagnifyingGlassIcon height="16" width="16" />
              </TextField.Slot>
            </TextField.Root>

            <Button
              disabled={!playerSearchId}
              onClick={() => searchPlayer()}
              loading={!!playerSearchId && isPending}>
              Search
            </Button>

            <Button
              disabled={!playerSearchId}
              onClick={() => setPlayerSearchId('')}
              color="gray"
              variant="soft">
              Clear
            </Button>
          </Flex>

          {errorMessage ? <Text color="red">{errorMessage}</Text> : null}
        </Flex>
      </Section>
    </Container>
  )
}
